"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContractsSection = void 0;
const ui_1 = require("@app/component/ui");
const const_1 = require("@app/const");
const graphql_1 = require("@app/gql-types/graphql");
const user_details_1 = require("@app/reducer/user-details");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const filtered_content_for_organization_1 = require("@app/util/filtered-content-for-organization");
const forms_1 = require("@app/util/forms/forms");
const useSortedContracts_1 = require("@app/util/hooks/useSortedContracts");
const use_modal_1 = require("@app/util/modal/use-modal");
const sentry_error_1 = require("@app/util/sentry/sentry-error");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const update_vacation_funds_1 = require("../../request/update-vacation-funds");
const vacation_funds_1 = require("../../request/vacation-funds");
const employee_card_context_1 = require("../../util/employee-card-context");
const useEmployeeCardFooterContent_1 = require("../../util/hooks/useEmployeeCardFooterContent");
const useEmployeeCardHeaderContent_1 = require("../../util/hooks/useEmployeeCardHeaderContent");
const useSubmitContracts_1 = require("../../util/hooks/useSubmitContracts");
const useUserDetailsContracts_1 = require("../../util/hooks/useUserDetailsContracts");
const useUserDetailsCustomData_1 = require("../../util/hooks/useUserDetailsCustomData");
const employee_contract_builder_1 = require("../employee-contract-builder");
const employee_contract_form_1 = require("../employee-contract-form");
const employee_vacation_form_1 = require("../employee-vacation-form");
const defaultFixedValues = {
    options: {
        minutesOfWorkPerMonth: 160 * 60,
        minutesOfWorkPerShift: 8 * 60,
        operation: 'singleShift',
    },
    period: {
        start: new Date(),
    },
};
const defaultOtherValues = {
    options: {
        minutesOfWorkPerMonth: 0,
        minutesOfWorkPerShift: 0,
        operation: 'singleShift',
    },
    period: {
        start: (0, moment_1.default)().format('YYYY-MM-DD'),
    },
};
const getDefaultWorkspaceContractType = (workspace) => {
    return workspace?.contractTypes?.find((ct) => ct.rules?.fixedWorkHours)?.id ?? '';
};
const assignLocalities = (contracts, localities) => {
    return contracts.map((c) => ({
        ...c,
        localities: localities ?? [],
    }));
};
const withNewContract = (contracts, defaultContract, userPositions) => {
    // NOTE: React Hook Form accidentally overwrites the `id` field. We need to map it to `cId`
    // also add localities while we're at it
    const mappedContracts = contracts.map((c) => ({
        ...c,
        cId: (0, maybe_unwrap_1.unwrapMaybe)(c.id) ?? '',
        localities: [],
    }));
    const newContract = {
        cId: 'new',
        localities: [],
        type: defaultContract,
        positions: userPositions.length === 1
            ? [
                {
                    created: userPositions[0].created,
                    positionId: userPositions[0].id,
                    skill: -1,
                    productionForecast: null,
                },
            ]
            : [],
        ...defaultFixedValues,
    };
    return [newContract, ...mappedContracts];
};
const processVacationsContractTypes = (vacations, contractTypes, userContracts) => {
    const contractTypesWithVacation = contractTypes?.filter((ct) => ct.rules.vacationFund === true) ?? [];
    const userContractTypesWithVacation = contractTypesWithVacation.filter((ctwv) => userContracts.some((uc) => ctwv.id === uc));
    const vacationContractTypes = userContractTypesWithVacation
        .map((ct) => Object.assign({}, vacations.find((v) => v.contractType === ct.id), { contract: ct }))
        .filter(util_1.isDefined);
    // TimeInput requires to pass minutes, not hours
    return vacationContractTypes.map(({ amount, ...rest }) => ({
        amount: (0, util_1.isValue)(amount)
            ? rest.unit === graphql_1.VacationUnit.Day
                ? amount
                : amount * 60
            : undefined,
        ...rest,
    }));
};
const ContractsSection = () => {
    const { workspace, employee, userIsMe, setIsLoading } = (0, employee_card_context_1.useEmployeeCard)();
    const { closeModal } = (0, use_modal_1.useModal)();
    const { canWrite } = (0, util_1.useAccessRole)();
    const [customData] = (0, useUserDetailsCustomData_1.useUserDetailsCustomData)();
    const isAllowedToEditVacationByOverride = (0, filtered_content_for_organization_1.getFilteredContentForOrganization)('edit_employee_vacation_fund', false, {});
    const allowEdit = canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES) && !customData?.managedByPlugin;
    const allowEditVacation = canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES) &&
        (!customData?.managedByPlugin || isAllowedToEditVacationByOverride);
    const nonArchivedPositions = (0, store_1.useRootSelector)((state) => state.positions.filter((p) => p.archived !== true));
    const contracts = (0, useUserDetailsContracts_1.useUserDetailsContracts)();
    const contractTypeFilters = contracts?.map((c) => (0, maybe_unwrap_1.unwrapMaybe)(c?.type)).filter(util_1.isDefined) ?? [];
    const dispatch = (0, store_1.useRootDispatch)();
    const [editing, _setEditing] = react_1.default.useState([]);
    const setEditing = react_1.default.useCallback((id) => {
        _setEditing((prev) => [...prev, id]);
    }, [_setEditing]);
    const { activeContracts, inactiveContracts, futureContracts } = (0, useSortedContracts_1.useSortedContracts)(contracts);
    const currentWorkPeriod = util_1.miscUtil
        .getMergedEventPeriods([...activeContracts, ...inactiveContracts])
        .find((per) => per.start.isSameOrBefore((0, moment_1.default)()) &&
        (!(0, util_1.isValue)(per.end) || per.end.isSameOrAfter((0, moment_1.default)())));
    // Pracovní poměr od {{since, date_formatted}} do {{until}}.
    // Since `until` can be null, it's difficult to assign the correct translated string in `date_formatted` format
    // By checking if the date is null, we can create correct translation using magic
    // t('{{value, date_formatted}}', { value: lastContract?.period?.end }) - t calls without any key, only with value and format
    const noActiveContract = (0, util_1.isUndefined)(activeContracts.at(0)) && (0, util_1.isUndefined)(activeContracts.at(-1));
    const contractSpanLabel = noActiveContract
        ? (0, util_1.t)('SORT_BY_CONTRACT_NO_CON')
        : (0, util_1.t)('CONTRACT_SPAN', {
            since: currentWorkPeriod?.start?.toJSON(),
            until: currentWorkPeriod?.end?.toJSON()
                ? (0, util_1.t)('{{value, date_formatted}}', {
                    value: currentWorkPeriod?.end?.toJSON(),
                })
                : (0, util_1.t)('EMPLOYEE_PROFILE_CONT_DATE_UNDEFINED'),
        });
    const contractTypesIncludingExternal = react_1.default.useMemo(() => [
        ...(workspace.contractTypes ?? []),
        // This seems like a bullshit, i dont know why this was here
        // {
        //   id: 'external',
        //   name: t('ORG_SP_SETTINGS_MODULES_EXTERNAL'),
        //   rules: {
        //     vacationFund: true,
        //     fixedWorkHours: false,
        //   },
        // },
    ], [workspace.contractTypes]);
    const contractTypes = contractTypesIncludingExternal?.map((c) => ({
        id: c.id,
        name: c.name,
        fixed: c.rules.fixedWorkHours,
    }));
    const { control, getValues, formState: { isDirty, isSubmitting }, handleSubmit, reset, setValue, } = (0, react_hook_form_1.useForm)({
        defaultValues: {
            contracts: withNewContract(contracts ?? [], getDefaultWorkspaceContractType(workspace), nonArchivedPositions),
        },
    });
    const { fields } = (0, react_hook_form_1.useFieldArray)({
        control,
        name: 'contracts',
    });
    react_1.default.useEffect(() => {
        const localities = workspace?.localities
            ?.filter((l) => l?.assigns?.find((la) => la.userId === (employee.user?.id ?? '')))
            .map(maybe_unwrap_1.unwrapMaybe)
            .filter(util_1.isDefined);
        reset({
            contracts: assignLocalities(withNewContract(contracts ?? [], getDefaultWorkspaceContractType(workspace), nonArchivedPositions), localities ?? []),
        });
    }, [contracts]);
    const submit = (0, useSubmitContracts_1.useSubmitContracts)(contracts, editing);
    const onSubmit = handleSubmit(async (data) => {
        setIsLoading(true);
        await submit(data);
        // role endpoints address the role by user id — an invite-pending employee
        // has no linked user yet, so the request could never succeed
        const userId = employee.user?.id;
        const workspaceId = workspace?.id;
        if (!(0, util_1.isValue)(userId) || !(0, util_1.isValue)(workspaceId)) {
            setIsLoading(false);
            throw new sentry_error_1.SentryError('contracts-section: userId or workspaceId cannot be null or undefined');
        }
        const processedVacations = data.vacations.map(({ contract, ...v }) => ({
            amount: workspace?.displayVacationsAsDays ? v.amount : (v?.amount ?? 0) / 60,
            unit: workspace?.displayVacationsAsDays ? graphql_1.VacationUnit.Day : graphql_1.VacationUnit.Hour,
            contractType: contract.id,
        }));
        const vacationFunds = await (0, update_vacation_funds_1.mutateUpdateVacationFunds)({
            workspaceId,
            userId,
            data: processedVacations,
        });
        dispatch((0, user_details_1.setUserVacation)(vacationFunds ?? []));
        if ((0, util_1.isDefined)(vacationFunds)) {
            const vacationContractTypes = processVacationsContractTypes(vacationFunds, contractTypesIncludingExternal, contractTypeFilters);
            injectValue('vacations', vacationContractTypes);
        }
        _setEditing([]);
        setIsLoading(false);
    });
    const newContractType = (0, react_hook_form_1.useWatch)({
        control,
        name: 'contracts.0.type',
    });
    react_1.default.useEffect(() => {
        const contractType = contractTypes?.find((ct) => ct.id === newContractType);
        setValue('contracts', [
            {
                cId: 'new',
                localities: [],
                type: contractType?.id,
                ...(contractType?.fixed ? defaultFixedValues : defaultOtherValues),
            },
            ...getValues().contracts.splice(1),
        ], { shouldDirty: false, shouldTouch: false });
    }, [newContractType]);
    const injectValue = (0, forms_1.useInjectValue)(setValue);
    react_1.default.useEffect(() => {
        const action = async () => {
            // no linked user (invite-pending employee) — no role to query vacations for
            const userId = employee.user?.id;
            if (!(0, util_1.isValue)(userId))
                return;
            const userVacations = await (0, vacation_funds_1.queryVacationFunds)({
                workspaceId: workspace?.id ?? '',
                userId,
            });
            dispatch((0, user_details_1.setUserVacation)(userVacations ?? []));
            const vacationContractTypes = processVacationsContractTypes(userVacations ?? [], contractTypesIncludingExternal, contractTypeFilters);
            // console.log({ userVacations, vacationContractTypes })
            injectValue('vacations', vacationContractTypes);
            reset(getValues(), { keepDirty: false, keepTouched: false, keepValues: true });
        };
        void action();
    }, [contracts]);
    const { fields: fieldsVacations } = (0, react_hook_form_1.useFieldArray)({
        control,
        name: 'vacations',
    });
    const isEditingNew = editing.includes('new');
    const { displayContent: displayFooterContent } = (0, useEmployeeCardFooterContent_1.useEmployeeCardFooterContent)(react_1.default.createElement("div", { style: { display: 'flex' } },
        react_1.default.createElement(ui_1.Button, { onClick: () => {
                closeModal();
                // void dispatch(setModal(null))
            } }, (0, util_1.t)('CLOSE')),
        react_1.default.createElement(ui_1.Button, { style: 'CONTAINED', disabled: (!isDirty ||
                isSubmitting ||
                (!userIsMe && !allowEdit && !allowEditVacation)) &&
                !isEditingNew, onClick: onSubmit }, (0, util_1.t)('SAVE'))));
    const { displayContent: displayHeaderContent } = (0, useEmployeeCardHeaderContent_1.useEmployeeCardHeaderContent)(allowEdit ? (react_1.default.createElement(ui_1.Button, { ico: editing.includes('new') ? 'cancel' : 'plus', style: 'BORDERLESS', onClick: () => _setEditing((prev) => {
            if (prev.includes('new')) {
                reset(getValues(), {
                    keepValues: true,
                    keepDirty: false,
                    keepDefaultValues: false,
                });
                return prev.filter((id) => id !== 'new');
            }
            return [...prev, 'new'];
        }) }, editing.includes('new') ? (0, util_1.t)('CANCEL') : (0, util_1.t)('ADD_CONTRACT'))) : null);
    console.log(activeContracts);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL },
            react_1.default.createElement(ui_1.Alert, { size: 'FULL_WIDTH', type: 'INFO', text: contractSpanLabel }),
            react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL }),
            editing.includes('new') && (react_1.default.createElement("div", { style: { display: 'flex', justifyContent: 'start' } },
                react_1.default.createElement(ui_1.Text, { weight: 'bold', text: (0, util_1.t)('ADD_CONTRACT') }))),
            editing.includes('new') && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(employee_contract_form_1.EmployeeContractForm, { index: 0, control: control, positions: nonArchivedPositions ?? [] }),
                react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL }))),
            react_1.default.createElement(employee_vacation_form_1.EmployeeVacationForm, { editing: editing, setEditing: setEditing, fields: fieldsVacations, control: control, isReadOnly: !allowEditVacation }),
            react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL }),
            react_1.default.createElement("div", { style: { display: 'flex', justifyContent: 'start' } },
                react_1.default.createElement(ui_1.Text, { weight: 'bold', text: util_1.miscUtil.getFilteredContentForOrganization('agreement_name_current', (0, util_1.t)('CURRENT_CONTRACTS')) })),
            react_1.default.createElement(employee_contract_builder_1.EmployeeContractBuilder, { contracts: activeContracts, userPositions: nonArchivedPositions, control: control, fields: fields, editing: editing, setEditing: setEditing, isReadOnly: !allowEdit }),
            react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL }),
            react_1.default.createElement("div", { style: { display: 'flex', justifyContent: 'start' } },
                react_1.default.createElement(ui_1.Text, { weight: 'bold', text: util_1.miscUtil.getFilteredContentForOrganization('agreement_name_future', (0, util_1.t)('FUTURE_CONTRACTS')) })),
            react_1.default.createElement(employee_contract_builder_1.EmployeeContractBuilder, { contracts: futureContracts, userPositions: nonArchivedPositions, control: control, fields: fields, editing: editing, setEditing: setEditing, isReadOnly: !allowEdit }),
            react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL }),
            react_1.default.createElement("div", { style: { display: 'flex', justifyContent: 'start' } },
                react_1.default.createElement(ui_1.Text, { weight: 'bold', text: util_1.miscUtil.getFilteredContentForOrganization('agreement_name_past', (0, util_1.t)('PAST_CONTRACTS')) })),
            react_1.default.createElement(employee_contract_builder_1.EmployeeContractBuilder, { contracts: inactiveContracts, userPositions: nonArchivedPositions, control: control, fields: fields, editing: editing, setEditing: setEditing, isReadOnly: !allowEdit })),
        displayHeaderContent(),
        displayFooterContent()));
};
exports.ContractsSection = ContractsSection;
